import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Crown, 
  ArrowRight, 
  ArrowLeft,
  Globe,
  QrCode,
  Clock,
  FileText,
  Image as ImageIcon,
  Share2,
  Sparkles,
  CheckCircle2,
  Play,
  Pause,
  ChevronLeft,
  ChevronRight,
  Zap
} from "lucide-react";

/**
 * ProMiniSitePreview Component
 * 
 * Animated slideshow preview of Pro Mini-Site features for Free users
 * Shows what they'll get when they upgrade, with "Upgrade Now" CTA at the end
 */
const ProMiniSitePreview = ({ 
  autoPlay = true,
  autoPlayInterval = 4000,
  onUpgrade,
  className = ""
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  const intervalRef = useRef(null);

  const slides = [
    {
      id: "intro",
      title: "Your Professional Mini-Site",
      subtitle: "Stand out from the competition",
      description: "Get your own branded page to showcase your work and accept bookings 24/7",
      icon: Globe,
      color: "from-amber-500 to-orange-500",
      preview: (
        <div className="bg-stone-800 rounded-lg p-4 h-48 flex flex-col">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full" />
            <div>
              <div className="h-4 w-32 bg-amber-500/30 rounded" />
              <div className="h-3 w-24 bg-stone-600 rounded mt-1" />
            </div>
          </div>
          <div className="flex-1 bg-stone-700/50 rounded-lg p-3">
            <div className="text-xs text-stone-400 text-center">
              myhairchair.com/p/<span className="text-amber-400">your-name</span>
            </div>
          </div>
          <div className="mt-3 h-8 bg-amber-500 rounded-lg animate-pulse" />
        </div>
      )
    },
    {
      id: "custom-url",
      title: "Custom URL",
      subtitle: "Your personal booking link",
      description: "Share your unique URL on social media, business cards, and more",
      icon: Globe,
      color: "from-blue-500 to-cyan-500",
      features: [
        "myhairchair.com/p/yourname",
        "Easy to remember",
        "Professional appearance"
      ],
      preview: (
        <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-lg p-4 h-48 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-stone-700 px-4 py-2 rounded-full mb-3">
              <Globe className="w-4 h-4 text-cyan-400" />
              <span className="text-white font-mono text-sm">myhairchair.com/p/</span>
              <span className="text-amber-400 font-mono text-sm font-bold">stylename</span>
            </div>
            <p className="text-stone-400 text-xs">Your personalized booking page</p>
          </div>
        </div>
      )
    },
    {
      id: "qr-code",
      title: "Shareable QR Code",
      subtitle: "Perfect for business cards & flyers",
      description: "Generate a branded QR code that links directly to your booking page",
      icon: QrCode,
      color: "from-purple-500 to-pink-500",
      features: [
        "Download as image",
        "Print on business cards",
        "Share on Instagram Stories"
      ],
      preview: (
        <div className="bg-stone-800 rounded-lg p-4 h-48 flex items-center justify-center">
          <div className="bg-white p-4 rounded-xl shadow-lg">
            <div className="w-24 h-24 grid grid-cols-5 gap-0.5">
              {Array(25).fill(0).map((_, i) => (
                <div 
                  key={i} 
                  className={`${Math.random() > 0.5 ? 'bg-black' : 'bg-white'}`}
                />
              ))}
            </div>
            <p className="text-center text-xs text-stone-600 mt-2 font-medium">Scan to Book</p>
          </div>
        </div>
      )
    },
    {
      id: "business-hours",
      title: "Business Hours",
      subtitle: "Let clients know when you're available",
      description: "Display your weekly schedule so clients book at the right times",
      icon: Clock,
      color: "from-green-500 to-emerald-500",
      features: [
        "7-day schedule display",
        "Custom hours per day",
        "Holiday notices"
      ],
      preview: (
        <div className="bg-stone-800 rounded-lg p-4 h-48">
          <p className="text-xs text-stone-400 mb-2 font-medium">Business Hours</p>
          <div className="space-y-1.5">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day, i) => (
              <div key={day} className="flex items-center justify-between text-xs">
                <span className="text-stone-400 w-8">{day}</span>
                <span className={i === 0 || i === 6 ? "text-red-400" : "text-green-400"}>
                  {i === 0 || i === 6 ? "CLOSED" : "9AM - 7PM"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "policies",
      title: "Booking Policies",
      subtitle: "Set expectations upfront",
      description: "Display cancellation, deposit, and late policies to reduce no-shows",
      icon: FileText,
      color: "from-orange-500 to-red-500",
      features: [
        "Cancellation policy",
        "Deposit requirements",
        "Late arrival rules"
      ],
      preview: (
        <div className="bg-stone-800 rounded-lg p-4 h-48">
          <p className="text-xs text-stone-400 mb-3 font-medium">Booking Policies</p>
          <div className="space-y-2">
            <div className="bg-stone-700/50 p-2 rounded">
              <p className="text-xs text-amber-400 font-medium">Cancellation</p>
              <p className="text-xs text-stone-300">2 hours notice required</p>
            </div>
            <div className="bg-stone-700/50 p-2 rounded">
              <p className="text-xs text-amber-400 font-medium">Deposit</p>
              <p className="text-xs text-stone-300">$10 to secure booking</p>
            </div>
            <div className="bg-stone-700/50 p-2 rounded">
              <p className="text-xs text-amber-400 font-medium">Late Policy</p>
              <p className="text-xs text-stone-300">15 min grace period</p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: "gallery",
      title: "Portfolio Gallery",
      subtitle: "Show off your best work",
      description: "Upload up to 5 of your best photos to attract new clients",
      icon: ImageIcon,
      color: "from-pink-500 to-rose-500",
      features: [
        "Up to 5 images",
        "High-quality display",
        "Mobile optimized"
      ],
      preview: (
        <div className="bg-stone-800 rounded-lg p-4 h-48">
          <p className="text-xs text-stone-400 mb-2 font-medium">Portfolio</p>
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div 
                key={i} 
                className={`aspect-square rounded-lg bg-gradient-to-br ${
                  i === 1 ? 'from-amber-500/40 to-orange-500/40' :
                  i === 2 ? 'from-pink-500/40 to-rose-500/40' :
                  i === 3 ? 'from-purple-500/40 to-indigo-500/40' :
                  i === 4 ? 'from-cyan-500/40 to-blue-500/40' :
                  'from-green-500/40 to-emerald-500/40'
                } flex items-center justify-center`}
              >
                <ImageIcon className="w-6 h-6 text-white/50" />
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: "cta",
      title: "Ready to Level Up?",
      subtitle: "Join thousands of successful stylists",
      description: "Get your Pro Mini-Site today and start attracting more clients",
      icon: Crown,
      color: "from-amber-500 to-orange-500",
      isCTA: true
    }
  ];

  // Auto-play logic
  useEffect(() => {
    if (isPlaying && !isHovered) {
      intervalRef.current = setInterval(() => {
        setCurrentSlide((prev) => {
          // Stop at the last slide (CTA)
          if (prev >= slides.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, autoPlayInterval);
    }
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, isHovered, autoPlayInterval]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsPlaying(false);
  };

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      const returnUrl = encodeURIComponent(window.location.pathname);
      navigate(`/membership?feature=minisite&tier=pro&return=${returnUrl}`);
    }
  };

  const currentSlideData = slides[currentSlide];
  const Icon = currentSlideData.icon;

  return (
    <div 
      className={`bg-gradient-to-br from-stone-900 to-stone-950 border border-stone-800 rounded-xl overflow-hidden ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-testid="minisite-preview"
    >
      {/* Header Badge */}
      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-amber-500/30 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-amber-400" />
          <span className="text-amber-400 text-sm font-medium">Pro Feature Preview</span>
        </div>
        <Badge className="bg-amber-500 text-black text-xs">
          {currentSlide + 1} / {slides.length}
        </Badge>
      </div>

      {/* Slide Content */}
      <div className="p-6">
        {/* Slide Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${currentSlideData.color} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-white mb-1">{currentSlideData.title}</h3>
            <p className="text-amber-400 text-sm font-medium">{currentSlideData.subtitle}</p>
          </div>
        </div>

        {/* Description */}
        <p className="text-stone-400 mb-4">{currentSlideData.description}</p>

        {/* Preview or CTA */}
        {currentSlideData.isCTA ? (
          <div className="text-center py-6">
            {/* Animated Icon */}
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/30 to-orange-500/30 rounded-full animate-ping" />
              <div className="relative w-24 h-24 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full flex items-center justify-center">
                <Crown className="w-12 h-12 text-white" />
              </div>
            </div>

            {/* Benefits */}
            <div className="flex flex-wrap justify-center gap-2 mb-6">
              {["Custom URL", "QR Code", "Gallery", "Policies", "Book Now Button"].map((item) => (
                <Badge key={item} variant="outline" className="text-stone-300 border-stone-600">
                  <CheckCircle2 className="w-3 h-3 mr-1 text-green-400" />
                  {item}
                </Badge>
              ))}
            </div>

            {/* Price */}
            <div className="bg-stone-800/50 rounded-lg p-4 mb-6 inline-block">
              <div className="flex items-center justify-center gap-2">
                <span className="text-4xl font-bold text-white">$29.99</span>
                <span className="text-stone-400">/month</span>
              </div>
              <p className="text-xs text-amber-400 mt-1">30-day FREE trial included</p>
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleUpgrade}
              size="lg"
              className="w-full max-w-xs bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-black font-bold h-14 text-lg"
              data-testid="minisite-preview-upgrade-btn"
            >
              <Zap className="w-5 h-5 mr-2" />
              Start 30-Day Free Trial
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        ) : (
          <>
            {/* Feature Preview */}
            {currentSlideData.preview}

            {/* Feature List */}
            {currentSlideData.features && (
              <div className="mt-4 flex flex-wrap gap-2">
                {currentSlideData.features.map((feature, i) => (
                  <Badge key={i} variant="outline" className="text-stone-300 border-stone-600">
                    <Sparkles className="w-3 h-3 mr-1 text-amber-400" />
                    {feature}
                  </Badge>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Navigation Controls */}
      <div className="border-t border-stone-800 px-4 py-3 flex items-center justify-between">
        {/* Progress Dots */}
        <div className="flex items-center gap-1.5">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className={`h-2 rounded-full transition-all ${
                i === currentSlide 
                  ? 'w-6 bg-amber-500' 
                  : 'w-2 bg-stone-600 hover:bg-stone-500'
              }`}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>

        {/* Play/Pause + Nav Arrows */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsPlaying(!isPlaying)}
            className="text-stone-400 hover:text-white h-8 w-8 p-0"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className="text-stone-400 hover:text-white disabled:opacity-30 h-8 w-8 p-0"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className="text-stone-400 hover:text-white disabled:opacity-30 h-8 w-8 p-0"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProMiniSitePreview;
